import React from 'react'
import { useDispatch } from 'react-redux'

import InputHandler from '@src/utilities/form-util'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { LocalAssetOptions } from '@store/types'

import { GenericModalCard as ModalCard } from '@components/modals'
import ToggleSwitch from '@components/toggle-switch'

import { ChevronSvg } from '@svg/react'

interface AassetHandlerProps {
  port: string
  assetOptions: LocalAssetOptions
}

function LocalAssetHandler({ port, assetOptions }: AassetHandlerProps) {
  const dispatch = useDispatch()

  const MAXIMUM_VALUE_FOR_IPv4_NETWORK_PORT = 65535

  const [toggled, setToggleModal] = React.useState(false)
  const [inputPortValue, setInputPortValue] = React.useState({
    value: port,
    isValid: true,
    message: '',
  })

  const [inputAssetOptionsValue, setInputAssetOptionsValue] =
    React.useState<LocalAssetOptions>(assetOptions)
  const disableAssetOptionsInput = React.useMemo(
    () => !inputPortValue?.value?.trim()?.length,
    [inputPortValue?.value]
  )
  const isUpdate = React.useMemo(() => {
    if (!port) {
      return false
    }
    if (inputPortValue.value !== port) {
      return true
    }
    if (inputAssetOptionsValue.localImages !== assetOptions.localImages) {
      return true
    }
    if (inputAssetOptionsValue.localVideos !== assetOptions.localVideos) {
      return true
    }
    return false
  }, [inputAssetOptionsValue, assetOptions, inputPortValue, port])

  const isPortValid = (value: string) =>
    Number(value) > 0 && Number(value) <= MAXIMUM_VALUE_FOR_IPv4_NETWORK_PORT

  const handleSave = () => {
    const value = inputPortValue.value.trim()
    dispatch(
      setProjectIdentity({
        assetPort: value,
        assetOptions: inputAssetOptionsValue,
      })
    )
  }

  const handleChangeLocalImagesInput = React.useCallback(() => {
    setInputAssetOptionsValue((prevVal) => ({
      ...prevVal,
      localImages: !prevVal.localImages,
    }))
  }, [])

  const handleChangeLocalVideosInput = React.useCallback(() => {
    setInputAssetOptionsValue((prevVal) => ({
      ...prevVal,
      localVideos: !prevVal.localVideos,
    }))
  }, [])

  const handleReset = () => {
    setInputPortValue({
      value: '',
      isValid: true,
      message: '',
    })
    setInputAssetOptionsValue({
      localImages: false,
      localVideos: false,
    })
  }

  const handleRemovePort = () => {
    handleReset()
    dispatch(
      setProjectIdentity({
        assetPort: '',
        assetOptions: {
          localImages: false,
          localVideos: false,
        },
      })
    )
  }

  React.useEffect(() => {
    setInputPortValue({
      ...inputPortValue,
      value: port,
    })
    setInputAssetOptionsValue(assetOptions)
  }, [toggled])

  return (
    <>
      <ModalCard
        headerLabel="Local Assets"
        okLabel={isUpdate ? 'Update' : 'Save'}
        toggle={(status: boolean) => setToggleModal(status)}
        cancelAction={handleReset}
        okAction={handleSave}
        isVisible={toggled}
        disableOk={inputPortValue.value.length === 0 || !inputPortValue.isValid}
        hideOk={!!port.length && !isUpdate}
      >
        <div className="px-4 text-left">
          <p>
            For improved performance you can choose to use an asset pack hosted
            on your local machine:
          </p>
          <p className="pt-4">
            <b>Port:</b>
          </p>
        </div>
        <div
          className={`mx-4 my-3 flex items-center justify-center rounded border text-lg ${
            !inputPortValue.isValid && inputPortValue.value
              ? 'border-red-700'
              : 'border-gray-300'
          } `}
        >
          <input
            onChange={(event) =>
              InputHandler(
                event,
                setInputPortValue,
                'The given port is invalid',
                isPortValid
              )
            }
            className="w-full border-none focus:ring-0"
            placeholder="Enter you local asset port"
            type="text"
            value={inputPortValue.value}
          />
          {port && (
            <button
              onClick={() => handleRemovePort()}
              type="button"
              className="ml-auto p-2 text-red-700"
            >
              Remove
            </button>
          )}
        </div>
        <div className="px-4 text-left text-sm">
          {!inputPortValue.isValid && inputPortValue.value && (
            <span className="ml-auto text-red-700">
              {inputPortValue.message}
            </span>
          )}
        </div>
        <div
          className={`mx-4 mt-4 flex flex-col gap-2 ${
            disableAssetOptionsInput ? 'opacity-50' : ''
          }`}
        >
          <div className="flex items-center rounded border border-gray-200 px-4 py-2">
            <span className="mr-auto">Local Images:</span>
            <ToggleSwitch
              key="localImagesInput"
              onChangeCallback={handleChangeLocalImagesInput}
              initialState={inputAssetOptionsValue.localImages}
              isDisabled={disableAssetOptionsInput}
            />
          </div>
          <div className="flex items-center rounded border border-gray-200 px-4 py-2">
            <span className="mr-auto">Local Videos:</span>
            <ToggleSwitch
              key="localVideosInput"
              onChangeCallback={handleChangeLocalVideosInput}
              initialState={inputAssetOptionsValue.localVideos}
              isDisabled={disableAssetOptionsInput}
            />
          </div>
        </div>
      </ModalCard>
      <button
        onClick={() => setToggleModal(!toggled)}
        type="button"
        className="flex w-full items-center rounded border border-2 border-gray-300 p-3 px-5 text-left"
      >
        <span className="text-sm">Use local asset pack</span>
        <ChevronSvg
          className="ml-auto h-5 w-5 py-1"
          stroke={4}
          rotate="right"
        />
      </button>
    </>
  )
}

export default LocalAssetHandler
